import React, { forwardRef } from 'react'

import { BaseLink } from '@sidecar-components/atoms/BaseLink'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { Tooltip } from '@sidecar-components/atoms/Tooltip'
import { Sentiment } from '@sidecar-components/types'
import { LinkType, LinkProps } from './Link.types'
import { iconStyles, textStyles } from './styles'

/**
 * The Link component is the primary way to link users to other pages or
 * external sites. We need the forwardRef so Links can be modal/menu triggers.
 *
 * @param {any} children - The content of the link
 * @param {string} className - Any additional classes to add to the link
 * @param {string} testId - Adds a data-testid param to the link for Heap
 *   tracking and automated testing.
 * @param {boolean} disabled - Whether or not the user can click the link
 * @param {string} icon - Name of icon that can be displayed to the left of the
 *   link text
 * @param {IconType} iconType - Whether the icon style should be "system" or
 *   "care"
 * @param {IconSize} iconSize - Size of the icon.
 * @param {function} onClick - Function fired on click of the link
 * @param {any} path - URL the user should be sent to
 * @param {boolean} removeStyles - If true, the link styles are not included and
 *   only the provided className is used. This should really only be used by the
 *   Button component with a path param.
 * @param {Sentiment} sentiment - What the point of the link should be
 * @param {string} target - Whether a link should open in a new window or not
 * @param {any} tooltip - Content that should be shown on hover/focus of the
 *   link
 * @param {LinkType} type - Whether the path is internal, external, or contact
 * @returns {React.ReactNode} - A link component
 */
export const Link = forwardRef(
  (
    {
      children,
      className = '',
      disabled = false,
      icon = '',
      iconPosition = 'before',
      iconType,
      iconSize = IconSize.SM,
      id = '',
      path = '',
      sentiment = Sentiment.DEFAULT,
      type = LinkType.INTERNAL,
      testId,
      tooltip,
      target = '_blank',
      onClick,
      removeStyles,
      ...rest
    }: LinkProps,
    forwardedRef: any,
  ) => {
    // If this link doesn't have a type, and isn't disabled (which then type
    // wouldn't matter), throw an error that they're already doomed.
    if (!disabled && !/external|internal|contact/.test(type) && !onClick) {
      throw new Error(
        `Link must either be disabled or have a type of 'contact', 'internal' or 'external' or have an onClick`,
      )
    }

    // if removeStyles is true, don't add the link styles to the Link. Only rely on the provided className.
    // This should really only be used by the Button component with a path param.
    const linkStyles = removeStyles ? null : textStyles({ sentiment, disabled })

    // the props for links are different depending on what type of link we're
    // rendering. All links have className and children in common, but it varies
    // after that.
    const defaultLinkProps = {
      className: `${linkStyles} ${className}`,
      children,
      onClick: onClick,
    }
    const linkProps = {
      external: {
        ...defaultLinkProps,
        href: path,
        target: target,
        rel: 'noopener noreferrer',
      },
      internal: {
        ...defaultLinkProps,
        to: path,
      },
      contact: {
        ...defaultLinkProps,
        href: path,
      },
    }

    // if this link has an optional icon, set that up for later
    const optionalIcon = icon ? (
      <Icon
        name={icon}
        size={iconSize}
        type={iconType}
        className={iconStyles({
          sentiment,
          iconSize,
          disabled,
        })}
      />
    ) : null

    const content = (
      <>
        {iconPosition === 'before' && optionalIcon}
        {children}
        {iconPosition === 'after' && optionalIcon}
      </>
    )

    // link content depends on several factors so we create it
    // here and then based on the type of link, set it up accordingly.
    let linkElement

    // If link is disabled, render a non-linking element
    if (disabled) {
      linkElement = (
        <span
          {...linkProps[type]}
          data-testid={testId}
          ref={forwardedRef}
          {...rest}
        >
          {content}
        </span>
      )
    }

    // An Internal link opens internal react-router-dom routes in the same tab.
    if (/internal/.test(type) && !disabled) {
      linkElement = (
        <BaseLink
          {...linkProps.internal}
          data-testid={testId}
          id={id}
          ref={forwardedRef}
          {...rest}
        >
          {content}
        </BaseLink>
      )
    }

    // An External link opens external paths in a new tab
    // A contact link is for tel: links, mailto: links, or to open the chat box.
    if (/external|contact/.test(type) && !disabled) {
      linkElement = (
        <a
          {...linkProps[type]}
          data-testid={testId}
          id={id}
          ref={forwardedRef}
          {...rest}
        >
          {content}
        </a>
      )
    }

    // Wrap link with tooltip if one is passed
    return tooltip ? (
      <Tooltip content={tooltip} addButtonWrapper={false}>
        {linkElement}
      </Tooltip>
    ) : (
      <React.Fragment>{linkElement}</React.Fragment>
    )
  },
)
